import './components/custom/plan-card-slider.js';
import './components/custom/plan-table.js';
import './components/custom/button.js';
import './components/custom/faq-accordion.js';
import {
    EVERY_30_DAYS,
    ANNUAL,
    SDK_LOG_PREFIX,
    FEATURES_API_ENDPOINT,
    PLANS_API_ENDPOINT,
    PROCESS_PLAN_API_ENDPOINT,
    getIntervalLabel,
    safeParse,
    emitEvent,
    escapeHTML,
    redirectTopWindow,
    getPlanCardsSkeletonMarkup,
    PLAN_CHANGE_MODAL_DEFAULT_TITLE,
    PLAN_CHANGE_MODAL_DEFAULT_DESCRIPTION,
    preparePlanChangeModalContent,
    createEmptyPlanChangeModalContent,
    getPlanChangeRequestFromEvent,
    isFreePlan,
    filterPlansByInterval,
    getSlidesPerView,
} from './helpers/helper.js';
import { VERSION } from './index.js';

/**
 * AppManagerBillingPage custom element
 * @property {Array} plans - List of plan objects
 * @property {Array} features - List of feature objects
 * @property {Object} currentPlan - The current plan object
 * @property {String} selectedInterval - Selected interval (monthly/annual)
 * @fires app-manager:select-plan - When a plan is selected
 */
class AppManagerBillingPage extends HTMLElement {
    static translations = {};

    static get observedAttributes() {
        return [
            'base-url',
            'shop-domain',
            'host',
            'translations',
            'discount-code',
            'show-only-highlights',
            'default-interval',
            'large-card',
            'show-free-on-yearly',
        ];
    }

    static translateMe(key) {
        const value = AppManagerBillingPage.translations[key] || key;
        if (typeof window !== 'undefined') {
            (window.__APP_MANAGER_TRANSLATIONS__ ??= new Set()).add(key);
        }
        return value;
    }

    constructor() {
        super();
        this.attachShadow({mode: 'open'});

        this._plans = [];
        this._features = [];
        this._faqs = [];
        this._currentPlan = {};
        this._shopifyPlan = '';
        this._selectedInterval = ANNUAL;
        this._promotionalDiscount = null;
        this._featureValues = {};
        this._hasActiveCharge = false;
        this._discountCode = null;
        this._host = null;
        this._baseUrl = null;
        this._shopDomain = null;
        this._defaultPlanId = null;
        this._chooseLater = false;
        this._onboard = false;
        this._isPlanChangeModalOpen = false;
        this._pendingPlanChange = null;
        this._pendingPlanChangeContent = { title: null, description: null, descriptionLines: [] };
        this._planTableShowFeatures = false;
        this._showOnlyHighlights = false;
        this._largeCard = false;
        this._showFreeOnYearly = false;
        this._filteredPlanCount = 0;
        this._defaultInterval = ANNUAL;
        this._isLoading = true;
        this._handleIntervalChange = this._handleIntervalChange.bind(this);
        this._handleMonthlyClick = this._handleMonthlyClick.bind(this);
        this._handleYearlyClick = this._handleYearlyClick.bind(this);
        this._handleTabKeydown = this._handleTabKeydown.bind(this);
        this._handleChooseLaterClick = this._handleChooseLaterClick.bind(this);
    }

    attributeChangedCallback(name, oldValue, newValue) {
        switch (name) {
            case 'base-url':
                this.baseUrl = newValue;
                break;
            case 'shop-domain':
                this.shopDomain = newValue;
                break;
            case 'host':
                this.host = newValue;
                break;
            case 'translations':
                AppManagerBillingPage.translations = safeParse(newValue, {});
                break;
            case 'discount-code':
                this.discountCode = newValue;
                break;
            case 'show-only-highlights':
                this._showOnlyHighlights = newValue !== null && newValue !== 'false';
                break;
            case 'default-interval':
                this._defaultInterval = newValue === 'monthly' ? EVERY_30_DAYS : ANNUAL;
                break;
            case 'large-card':
                this._largeCard = newValue !== null && newValue !== 'false';
                break;
            case 'show-free-on-yearly':
                this._showFreeOnYearly = newValue !== null && newValue !== 'false';
                break;
        }
    }

    // --- Property Getters/Setters ---
    get plans() { return this._plans; }
    set plans(value) { this._plans = value; }

    get features() { return this._features; }
    set features(value) { this._features = value; }

    get faqs() { return this._faqs; }
    set faqs(value) { this._faqs = Array.isArray(value) ? value : []; }

    get currentPlan() { return this._currentPlan; }
    set currentPlan(value) { this._currentPlan = value || {}; }

    get shopifyPlan() { return this._shopifyPlan; }
    set shopifyPlan(value) { this._shopifyPlan = value; }

    get selectedInterval() { return this._selectedInterval; }
    set selectedInterval(value) { this._selectedInterval = value; }

    get promotionalDiscount() { return this._promotionalDiscount; }
    set promotionalDiscount(value) { this._promotionalDiscount = value; }

    get featureValues() { return this._featureValues; }
    set featureValues(value) { this._featureValues = value; }

    get discountCode() { return this._discountCode; }
    set discountCode(value) { this._discountCode = value; }

    get host() { return this._host; }
    set host(value) { this._host = value; }

    get baseUrl() { return this._baseUrl; }
    set baseUrl(value) { this._baseUrl = value; }

    get shopDomain() { return this._shopDomain; }
    set shopDomain(value) { this._shopDomain = value; }

    get hasActiveCharge() { return this._hasActiveCharge; }
    set hasActiveCharge(value) { this._hasActiveCharge = !!value; }

    get defaultPlanId() { return this._defaultPlanId; }
    set defaultPlanId(value) { this._defaultPlanId = value; }

    get chooseLater() { return this._chooseLater; }
    set chooseLater(value) { this._chooseLater = !!value; }

    get onboard() { return this._onboard; }
    set onboard(value) {
        if (this._onboard !== !!value) {
            this._onboard = !!value;
            try { this._render(); } catch (e) { console.error(SDK_LOG_PREFIX, 'Error in _render (onboard setter):', e); }
        }
    }

    // --- Helper: Prepare API Params ---
    _prepareApiParams(extra = {}) {
        const params = {
            shop_domain: this.shopDomain,
            ...extra
        };
        if (this.discountCode) params['discount_code'] = this.discountCode;
        if (this.host) params['host'] = this.host;
        return params;
    }

    // --- Helper: Build Query String ---
    _buildQueryString(params) {
        return Object.entries(params)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('&');
    }

    async connectedCallback() {
        this.baseUrl = this.getAttribute('base-url');
        this.shopDomain = this.getAttribute('shop-domain');
        this.host = this.getAttribute('host');
        this.discountCode = this.getAttribute('discount-code');
        this._validateRequiredProps();
        this._isLoading = true;
        this._renderSkeleton();
        this._onResize = () => {
            if (!this._isLoading) this._syncContainerWidths();
        };
        window.addEventListener('resize', this._onResize);
        try {
            await this._fetchFeatures();
            await this._fetchPlans();
        } finally {
            this._isLoading = false;
            this._render();
        }
    }

    disconnectedCallback() {
        if (this._onResize) window.removeEventListener('resize', this._onResize);
        this._removeEventListeners();
    }

    async _fetchFeatures() {
        try {
            const url = `${this.baseUrl}${FEATURES_API_ENDPOINT}`;
            const response = await fetch(url);
            if (!response.ok) {
                console.error(SDK_LOG_PREFIX, 'Failed to fetch features:', response.statusText || response.status);
                return;
            }
            const data = await response.json();
            if (!data || !Array.isArray(data.features)) return;
            this.features = data.features.filter((item) => item && item.hidden_feature !== true)
                .sort((a, b) => parseInt(a.display_order, 10) - parseInt(b.display_order, 10));
            this.features.forEach((feature) => {
                if (feature && feature.value_type === 'array') {
                    this.featureValues[feature.uuid] = feature.values;
                }
            });
        } catch (error) {
            console.error(SDK_LOG_PREFIX, 'Error in _fetchFeatures:', error);
        }
    }

    async _fetchPlans() {
        if (!this._validateRequiredProps()) return;
        try {
            const params = this._prepareApiParams();

            // --- Append frontend sdk version to param ---
            params['frontend_sdk_version'] = VERSION;

            const url = `${this.baseUrl}${PLANS_API_ENDPOINT}?${this._buildQueryString(params)}`;
            const response = await fetch(url);
            if (!response.ok) {
                console.error(SDK_LOG_PREFIX, 'Failed to fetch plans:', response.statusText);
                return;
            }
            const data = await response.json();
            if (data && Array.isArray(data.plans) && data.plans.length) {
                this.plans = data.plans.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
                this.currentPlan = data.plan && typeof data.plan === 'object' ? data.plan : {};
                const hasActiveCharge = !!data.has_active_charge;
                const currentPlanInterval = this.currentPlan?.interval;
                const hasAnnualPlans = Array.isArray(data.plans) && data.plans.some(plan => plan.interval === ANNUAL);
                if (!hasAnnualPlans) {
                    this.selectedInterval = EVERY_30_DAYS;
                } else if (hasActiveCharge && currentPlanInterval) {
                    this.selectedInterval = currentPlanInterval;
                } else {
                    this.selectedInterval = this._defaultInterval;
                }
                this.shopifyPlan = typeof data.shopify_plan === 'string' ? data.shopify_plan : '';
                this.promotionalDiscount = (data.promotional_discount && typeof data.promotional_discount === 'object' && Object.keys(data.promotional_discount).length > 0) ? data.promotional_discount : {};
                this.hasActiveCharge = hasActiveCharge;
                this.featureValues = this.featureValues || {};
                this.faqs = Array.isArray(data.app_faqs) ? data.app_faqs : [];
                this.defaultPlanId = data.default_plan_id || null;
                this.chooseLater = !!data.choose_later;
                this.onboard = this.defaultPlanId && this.chooseLater;
                try { this._render(); } catch (e) { console.error(SDK_LOG_PREFIX, 'Error in _render (_fetchPlans):', e); }
            }
        } catch (error) {
            console.error(SDK_LOG_PREFIX, 'Error in _fetchPlans:', error);
        }
    }

    async _fetchPlanURL(plan) {
        try {
            if (!plan || !plan.id) {
                console.error(SDK_LOG_PREFIX, 'Invalid plan object for _fetchPlanURL:', plan);
                return;
            }
            const params = this._prepareApiParams({ shop: this.shopDomain });
            const queryString = this._buildQueryString(params);
            const url = `${this.baseUrl}${PROCESS_PLAN_API_ENDPOINT}/${plan.id}?${queryString}`;
            const response = await fetch(url);
            if (!response.ok) {
                console.error(SDK_LOG_PREFIX, 'Failed to fetch plan URL:', response.statusText || response.status);
                return;
            }
            const data = await response.json();
            if (data?.plan_type === 'free_plan') {
                emitEvent(this, 'app-manager:plan-select', {
                    free_plan: true,
                    plan_id: plan.id,
                    interval: plan.interval || this.selectedInterval,
                    plan,
                });
            } else {
                const redirectUrl = data?.redirect_url;
                if (redirectUrl) {
                    redirectTopWindow(redirectUrl);
                } else {
                    console.error(SDK_LOG_PREFIX, 'No redirect URL found in response');
                }
            }
        } catch (error) {
            console.error(SDK_LOG_PREFIX, 'Error in _fetchPlanURL:', error);
        }
    }

    _validateRequiredProps() {
        if (!this.baseUrl || !this.shopDomain) {
            console.error(SDK_LOG_PREFIX, 'base-url and shop-domain attributes are required');
            return false;
        }
        return true;
    }

    _openPlanChangeModal(plan, content = {}) {
        if (!plan || !plan.id) return;
        this._pendingPlanChange = plan;
        this._pendingPlanChangeContent = preparePlanChangeModalContent(content, {
            title: PLAN_CHANGE_MODAL_DEFAULT_TITLE,
            description: PLAN_CHANGE_MODAL_DEFAULT_DESCRIPTION,
        });
        this._isPlanChangeModalOpen = true;
        this._render();
    }

    _closePlanChangeModal() {
        this._isPlanChangeModalOpen = false;
        this._pendingPlanChange = null;
        this._pendingPlanChangeContent = createEmptyPlanChangeModalContent();
        this._render();
    }

    _renderPlanChangeDescriptionHTML(lines) {
        return lines
            .map(line => `<p class="billing-modal__description">${escapeHTML(AppManagerBillingPage.translateMe(line))}</p>`)
            .join('');
    }

    _handleIntervalChange(e) {
        this.selectedInterval = e.target.value;
        this._render();
    }

    _handleMonthlyClick() {
        this.selectedInterval = EVERY_30_DAYS;
        this._render();
    }

    _handleYearlyClick() {
        this.selectedInterval = ANNUAL;
        this._render();
    }

    _handleTabKeydown(e) {
        const navigationKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (!navigationKeys.includes(e.key)) return;
        const tabs = Array.from(this.shadowRoot?.querySelectorAll('.billing-tabs__tab') || []);
        if (tabs.length < 2) return;
        const currentIndex = tabs.indexOf(e.currentTarget);
        if (currentIndex === -1) return;
        e.preventDefault();
        let nextIndex;
        if (e.key === 'Home') {
            nextIndex = 0;
        } else if (e.key === 'End') {
            nextIndex = tabs.length - 1;
        } else {
            const step = e.key === 'ArrowRight' ? 1 : -1;
            nextIndex = (currentIndex + step + tabs.length) % tabs.length;
        }
        tabs[nextIndex].click();
        // the click re-renders the page, so re-focus the tab that is now selected
        this.shadowRoot?.querySelector('.billing-tabs__tab--selected')?.focus();
    }

    async _handleChooseLaterClick() {
        try {
            const url = `${this.baseUrl}/api/app-manager/active-without-plan`;
            const payload = {
                shop_domain: this.shopDomain,
                plan_id: this.defaultPlanId
            };
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.status === true && this.onboard) {
                emitEvent(this, 'app-manager:plan-select', { choose_later: true });
                this.onboard = false;
            }
        } catch (error) {
            console.error(SDK_LOG_PREFIX, error);
        }
    }

    _renderEmptyState() {
        if (!this.shadowRoot) return;
        this.shadowRoot.innerHTML = `
            <style>
                :host {
                    all: initial;
                    font-family: -apple-system, BlinkMacSystemFont, San Francisco, Roboto, Segoe UI, Helvetica Neue, sans-serif;
                    font-size: 16px !important;
                }
                .am-empty {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-height: 240px;
                    padding: 32px 16px;
                    text-align: center;
                    color: #616161;
                    font-size: 14px;
                    line-height: 20px;
                }
            </style>
            <div class="am-empty" role="status">${escapeHTML(AppManagerBillingPage.translateMe('No plans are available right now.'))}</div>
        `;
    }

    _render() {
        try {
            if (!this._isLoading && (!Array.isArray(this.plans) || this.plans.length === 0)) {
                this._renderEmptyState();
                return;
            }

            const existingPlanTable = this.shadowRoot?.querySelector?.('plan-table');
            if (existingPlanTable && typeof existingPlanTable.showFeatures === 'boolean') {
                this._planTableShowFeatures = existingPlanTable.showFeatures;
            }

            const filteredPlans = filterPlansByInterval(this.plans, this.selectedInterval, this._showFreeOnYearly);
            this._filteredPlanCount = filteredPlans.length;
            const modalTitle = this._pendingPlanChangeContent?.title || PLAN_CHANGE_MODAL_DEFAULT_TITLE;
            const modalDescriptionLines = this._pendingPlanChangeContent?.descriptionLines?.length
                ? this._pendingPlanChangeContent.descriptionLines
                : [PLAN_CHANGE_MODAL_DEFAULT_DESCRIPTION];
            // Check if there is at least one annual plan
            const hasAnnualPlan = (this.plans || []).some(p => p.interval === ANNUAL);

            this.shadowRoot.innerHTML = `
                <style>
                    :host {
                        all: initial;
                        font-family: -apple-system, BlinkMacSystemFont, San Francisco, Roboto, Segoe UI, Helvetica Neue, sans-serif;
                        font-size: 16px !important;
                    }
                    .billing-tabs {
                        display: flex;
                        margin: 32px auto 16px auto;
                    }
                    .billing-tabs__list {
                        display: inline-flex;
                        align-items: center;
                        gap: 4px;
                        padding: 4px;
                        background-color: #ffffff;
                        border: 1px solid #e3e3e3;
                        border-radius: 12px;
                    }
                    .billing-tabs__tab {
                        appearance: none;
                        border: 0;
                        background-color: transparent;
                        border-radius: 8px;
                        padding: 4px 12px;
                        height: 28px;
                        display: inline-flex;
                        align-items: center;
                        gap: 4px;
                        font-family: Inter, -apple-system, BlinkMacSystemFont, 'San Francisco', 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
                        font-size: 12px;
                        line-height: 16px;
                        font-weight: 550;
                        color: #4a4a4a;
                        white-space: nowrap;
                        cursor: pointer;
                        transition: background-color 150ms ease, color 150ms ease;
                    }
                    .billing-tabs__tab--selected {
                        background-color: rgba(0, 0, 0, 0.08);
                        color: #303030;
                    }
                    .billing-tabs__tab:hover {
                        background-color: rgba(0, 0, 0, 0.05);
                        color: #4a4a4a;
                    }
                    .billing-tabs__tab:focus-visible {
                        outline: 2px solid #005bd3;
                        outline-offset: 1px;
                    }
                    .billing-tabs__savings {
                        font-weight: 650;
                    }
                    .choose-later-wrapper {
                        display: flex;
                        justify-content: center;
                        margin: 24px 0 0 0;
                    }
                    .app-manager__width-container,
                    .app-manager__width-container *,
                    .app-manager__width-container *::before,
                    .app-manager__width-container *::after {
                        box-sizing: border-box;
                    }
                    .app-manager__width-container {
                        padding-left: 96px;
                        padding-right: 96px;
                    }
                    @media screen and (max-width: 768px) {
                        .app-manager__width-container {
                            max-width: 100% !important;
                            padding-left: 0;
                            padding-right: 0;
                        }
                    }

                    .billing-modal__backdrop {
                        position: fixed;
                        inset: 0;
                        background: rgba(48, 48, 48, 0.5);
                        display: none;
                        align-items: center;
                        justify-content: center;
                        padding: 16px;
                        z-index: 2147483000;
                    }
                    .billing-modal__backdrop--open {
                        display: flex;
                    }
                    .billing-modal {
                        width: min(100%, 620px);
                        background: #fff;
                        border-radius: 12px;
                        box-shadow: 0 20px 56px rgba(0, 0, 0, 0.22);
                        border: 1px solid #e3e3e3;
                        overflow: hidden;
                    }
                    .billing-modal__header {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        padding: 16px;
                        border-bottom: 1px solid #e3e3e3;
                        background: #f3f3f3;
                    }
                    .billing-modal__title {
                        margin: 0;
                        font-size: 14px;
                        line-height: 20px;
                        font-weight: 600;
                        color: #303030;
                    }
                    .billing-modal__close {
                        border: 0;
                        background: transparent;
                        width: 28px;
                        border-radius: 8px;
                        cursor: pointer;
                        color: #606060;
                    }
                    .billing-modal__content {
                        padding: 16px;
                    }
                    .billing-modal__description {
                        margin: 0;
                        font-size: 13px;
                        line-height: 20px;
                        color: #303030;
                    }
                    .billing-modal__description + .billing-modal__description {
                        margin-top: 16px;
                    }
                    .billing-modal__footer {
                        display: flex;
                        justify-content: flex-end;
                        gap: 8px;
                        padding: 16px 20px;
                        border-top: 1px solid #e3e3e3;
                    }

                    .faq-section {
                        margin: 32px auto;
                    }
                    .faq-section__title {
                        margin-bottom: 16px;
                        font-size: 14px;
                        font-weight: 600;
                        color: #303030;
                        line-height: 20px;
                    }
                    @media screen and (max-width: 768px) {
                        .faq-section {
                            margin: 24px auto;
                        }
                    }
                </style>

                <div class="billing-page">
                    ${hasAnnualPlan ? `
                        <div class="billing-tabs app-manager__width-container">
                            <div class="billing-tabs__list" role="tablist" aria-label="${AppManagerBillingPage.translateMe('Billing interval')}">
                                <button type="button" role="tab" id="monthly-btn" class="billing-tabs__tab${this.selectedInterval === EVERY_30_DAYS ? ' billing-tabs__tab--selected' : ''}" aria-selected="${this.selectedInterval === EVERY_30_DAYS ? 'true' : 'false'}" tabindex="${this.selectedInterval === EVERY_30_DAYS ? '0' : '-1'}">
                                    ${AppManagerBillingPage.translateMe('Billed')} ${getIntervalLabel(EVERY_30_DAYS)}
                                </button>
                                <button type="button" role="tab" id="yearly-btn" class="billing-tabs__tab${this.selectedInterval === ANNUAL ? ' billing-tabs__tab--selected' : ''}" aria-selected="${this.selectedInterval === ANNUAL ? 'true' : 'false'}" tabindex="${this.selectedInterval === ANNUAL ? '0' : '-1'}">
                                    ${AppManagerBillingPage.translateMe('Billed')} ${getIntervalLabel(ANNUAL)}
                                    <span class="billing-tabs__savings">${AppManagerBillingPage.translateMe('17%')} ${AppManagerBillingPage.translateMe('Off')}</span>
                                </button>
                            </div>
                        </div>
                    ` : ''}

                    <plan-card-slider></plan-card-slider>
                    <plan-table></plan-table>
                    
                    ${this._faqs && this._faqs.length > 0 ? `
                        <div class="app-manager__width-container faq-section">
                            <h2 class="faq-section__title">${AppManagerBillingPage.translateMe('Frequently asked questions')}</h2>
                            <faq-accordion></faq-accordion>
                        </div>
                    ` : ''}
                    
                    ${this.onboard ? `
                        <div class="choose-later-wrapper">
                            <a href="#" class="choose-later-anchor" style="color: inherit; text-decoration: underline; cursor: pointer;">${AppManagerBillingPage.translateMe('I will choose the plan later')}</a>
                        </div>
                    ` : ''}

                    <div class="billing-modal__backdrop ${this._isPlanChangeModalOpen ? 'billing-modal__backdrop--open' : ''}" id="billing-plan-change-modal-backdrop" aria-hidden="${this._isPlanChangeModalOpen ? 'false' : 'true'}">
                        <div class="billing-modal" role="dialog" aria-modal="true" aria-labelledby="billing-plan-change-modal-title" aria-describedby="billing-plan-change-modal-description">
                            <div class="billing-modal__header">
                                <h2 class="billing-modal__title" id="billing-plan-change-modal-title">${escapeHTML(AppManagerBillingPage.translateMe(modalTitle))}</h2>
                                <button class="billing-modal__close" id="billing-plan-change-modal-close" aria-label="${escapeHTML(AppManagerBillingPage.translateMe('Close'))}">
                                    <svg viewBox="0 0 16 16" class="Polaris-Icon__Svg" focusable="false" aria-hidden="true"><path d="M13.97 15.03a.75.75 0 1 0 1.06-1.06l-3.97-3.97 3.97-3.97a.75.75 0 0 0-1.06-1.06l-3.97 3.97-3.97-3.97a.75.75 0 0 0-1.06 1.06l3.97 3.97-3.97 3.97a.75.75 0 1 0 1.06 1.06l3.97-3.97 3.97 3.97Z"></path></svg>
                                </button>
                            </div>
                            <div class="billing-modal__content">
                                <div id="billing-plan-change-modal-description">${this._renderPlanChangeDescriptionHTML(modalDescriptionLines)}</div>
                            </div>
                            <div class="billing-modal__footer">
                                <app-button id="billing-plan-change-modal-cancel" variant="secondary">${AppManagerBillingPage.translateMe('Cancel')}</app-button>
                                <app-button id="billing-plan-change-modal-confirm" variant="primary">${AppManagerBillingPage.translateMe('Change Plan')}</app-button>
                            </div>
                        </div>
                    </div>
                </div>
            `;

            // Set properties on child components
            const planCardSlider = this.shadowRoot.querySelector('plan-card-slider');
            if (planCardSlider) {
                planCardSlider.plans = filteredPlans;
                planCardSlider.features = this.features;
                planCardSlider.currentPlan = this.currentPlan;
                planCardSlider.promotionalDiscount = this.promotionalDiscount;
                planCardSlider.selectedInterval = this.selectedInterval;
                planCardSlider.hasActiveCharge = this.hasActiveCharge;
                planCardSlider.shopifyPlan = this.shopifyPlan;
                planCardSlider.showOnlyHighlights = this._showOnlyHighlights;
                planCardSlider.largeCard = this._largeCard;
            }
            const planTable = this.shadowRoot.querySelector('plan-table');
            if (planTable) {
                planTable.plans = filteredPlans;
                planTable.features = this.features;
                planTable.currentPlan = this.currentPlan;
                planTable.promotionalDiscount = this.promotionalDiscount;
                planTable.featureValues = this.featureValues;
                planTable.selectedInterval = this.selectedInterval;
                planTable.hasActiveCharge = this.hasActiveCharge;
                planTable.shopifyPlan = this.shopifyPlan;
                planTable.showFeatures = this._planTableShowFeatures;
                planTable.largeCard = this._largeCard;
            }

            const faqAccordion = this.shadowRoot.querySelector('faq-accordion');
            if (faqAccordion) {
                faqAccordion.faqs = this.faqs;
            }

            this._syncContainerWidths();
            this._addEventListeners();

            if (this.onboard) {
                const chooseLaterLink = this.shadowRoot.querySelector('.choose-later-wrapper a.choose-later-anchor');
                if (chooseLaterLink) {
                    chooseLaterLink.addEventListener('click', this._handleChooseLaterClick);
                }
            }
        } catch (e) {
            console.error(SDK_LOG_PREFIX, 'Error in _render:', e);
        }
    }

    _renderSkeleton() {
        if (!this.shadowRoot) return;
        try {
            this.shadowRoot.innerHTML = getPlanCardsSkeletonMarkup(AppManagerBillingPage.translateMe('Loading plans'));
        } catch (e) {
            console.error(SDK_LOG_PREFIX, 'Error in _renderSkeleton:', e);
        }
    }

    _syncContainerWidths() {
        const effectiveSlides = getSlidesPerView(this._filteredPlanCount, this._largeCard);
        const maxWidths = {1: 680, 2: 808, 3: 1208, 4: 1448, 5: 1608};
        const maxWidth = maxWidths[effectiveSlides] ? `${maxWidths[effectiveSlides]}px` : '';
        this.shadowRoot?.querySelectorAll('.app-manager__width-container').forEach(el => {
            el.style.maxWidth = maxWidth;
        });
    }

    // Event binding
    _addEventListeners() {
        if (!this.shadowRoot) return;
        this._removeEventListeners();
        const monthlyBtn = this.shadowRoot.querySelector('#monthly-btn');
        const yearlyBtn = this.shadowRoot.querySelector('#yearly-btn');
        if (monthlyBtn) {
            monthlyBtn.addEventListener('click', this._handleMonthlyClick);
            monthlyBtn.addEventListener('keydown', this._handleTabKeydown);
        }
        if (yearlyBtn) {
            yearlyBtn.addEventListener('click', this._handleYearlyClick);
            yearlyBtn.addEventListener('keydown', this._handleTabKeydown);
        }
        const planCardSlider = this.shadowRoot.querySelector('plan-card-slider');
        if (planCardSlider) {
            planCardSlider.addEventListener('app-manager:plan-click', this._handlePlanClick);
            planCardSlider.addEventListener('app-manager:plan-change-confirmation-request', this._handlePlanChangeConfirmationRequest);
        }
        const planTable = this.shadowRoot.querySelector('plan-table');
        if (planTable) {
            planTable.addEventListener('app-manager:plan-click', this._handlePlanClick);
            planTable.addEventListener('app-manager:plan-change-confirmation-request', this._handlePlanChangeConfirmationRequest);
        }

        const modalBackdrop = this.shadowRoot.querySelector('#billing-plan-change-modal-backdrop');
        const modalClose = this.shadowRoot.querySelector('#billing-plan-change-modal-close');
        const modalCancel = this.shadowRoot.querySelector('#billing-plan-change-modal-cancel');
        const modalConfirm = this.shadowRoot.querySelector('#billing-plan-change-modal-confirm');
        if (modalBackdrop) {
            modalBackdrop.addEventListener('click', this._handlePlanChangeModalBackdropClick);
        }
        if (modalClose) {
            modalClose.addEventListener('click', this._handlePlanChangeModalCancel);
        }
        if (modalCancel) {
            modalCancel.addEventListener('app-button-click', this._handlePlanChangeModalCancel);
        }
        if (modalConfirm) {
            modalConfirm.addEventListener('app-button-click', this._handlePlanChangeModalConfirm);
        }
    }

    _removeEventListeners() {
        if (!this.shadowRoot) return;
        const monthlyBtn = this.shadowRoot.querySelector('#monthly-btn');
        const yearlyBtn = this.shadowRoot.querySelector('#yearly-btn');
        if (monthlyBtn) {
            monthlyBtn.removeEventListener('click', this._handleMonthlyClick);
            monthlyBtn.removeEventListener('keydown', this._handleTabKeydown);
        }
        if (yearlyBtn) {
            yearlyBtn.removeEventListener('click', this._handleYearlyClick);
            yearlyBtn.removeEventListener('keydown', this._handleTabKeydown);
        }
        const planCardSlider = this.shadowRoot.querySelector('plan-card-slider');
        if (planCardSlider) {
            planCardSlider.removeEventListener('app-manager:plan-click', this._handlePlanClick);
            planCardSlider.removeEventListener('app-manager:plan-change-confirmation-request', this._handlePlanChangeConfirmationRequest);
        }
        const planTable = this.shadowRoot.querySelector('plan-table');
        if (planTable) {
            planTable.removeEventListener('app-manager:plan-click', this._handlePlanClick);
            planTable.removeEventListener('app-manager:plan-change-confirmation-request', this._handlePlanChangeConfirmationRequest);
        }

        const modalBackdrop = this.shadowRoot.querySelector('#billing-plan-change-modal-backdrop');
        const modalClose = this.shadowRoot.querySelector('#billing-plan-change-modal-close');
        const modalCancel = this.shadowRoot.querySelector('#billing-plan-change-modal-cancel');
        const modalConfirm = this.shadowRoot.querySelector('#billing-plan-change-modal-confirm');
        if (modalBackdrop) {
            modalBackdrop.removeEventListener('click', this._handlePlanChangeModalBackdropClick);
        }
        if (modalClose) {
            modalClose.removeEventListener('click', this._handlePlanChangeModalCancel);
        }
        if (modalCancel) {
            modalCancel.removeEventListener('app-button-click', this._handlePlanChangeModalCancel);
        }
        if (modalConfirm) {
            modalConfirm.removeEventListener('app-button-click', this._handlePlanChangeModalConfirm);
        }
    }

    _handlePlanChangeConfirmationRequest = (e) => {
        const payload = getPlanChangeRequestFromEvent(e, ['plan-card-slider', 'plan-table']);
        if (!payload) return;
        this._openPlanChangeModal(payload.plan, payload.content);
    }

    _handlePlanChangeModalBackdropClick = (e) => {
        if (e.target && e.target.id === 'billing-plan-change-modal-backdrop') {
            this._closePlanChangeModal();
        }
    }

    _handlePlanChangeModalCancel = () => {
        this._closePlanChangeModal();
    }

    _handlePlanChangeModalConfirm = () => {
        const plan = this._pendingPlanChange;
        this._closePlanChangeModal();
        if (plan?.id) {
            emitEvent(this, 'app-manager:plan-click', {
                plan,
                plan_id: plan.id,
                interval: plan.interval || this.selectedInterval,
            });
            this._fetchPlanURL(plan).catch(err => console.error('Error fetching plan URL:', err));
        }
    }

    _handlePlanClick = (e) => {
        console.log('Plan clicked:', e.detail.plan?.id);
        this._fetchPlanURL(e.detail.plan)
            .then(() => {})
            .catch(err => console.error('Error fetching plan URL:', err));
    }
}

if (!customElements.get('app-manager-billing-page')) {
    customElements.define('app-manager-billing-page', AppManagerBillingPage);
}

export {AppManagerBillingPage};